/**
 * Workspace settings navigation — which sections exist, which a user may see,
 * and which one a URL asks for. Shared by the page and its loading skeleton so
 * the skeleton draws exactly the nav (and active item) the page will.
 */

export type WorkspaceSettingsSection = 'profile' | 'brand-websites' | 'mcp' | 'ai-knowledge';

export type WorkspaceSettingsNavUser = {
  role?: string | null;
  is_organization_owner?: boolean | null;
  is_super_admin?: boolean | null;
} | null | undefined;

export const WORKSPACE_SETTINGS_SECTIONS: ReadonlyArray<{ id: WorkspaceSettingsSection; label: string }> = [
  { id: 'profile', label: 'Workspace profile' },
  { id: 'mcp', label: 'MCP' },
  { id: 'brand-websites', label: 'Brand websites' },
  { id: 'ai-knowledge', label: 'AI Knowledge' },
];

export function parseWorkspaceSettingsSection(raw: string | null): WorkspaceSettingsSection {
  if (raw === 'brand-websites' || raw === 'workspace') return 'brand-websites';
  if (raw === 'mcp' || raw === 'ai-knowledge') return raw;
  return 'profile';
}

/** MCP is for org owners and admins — never super admins browsing an org. */
export function canSeeWorkspaceMcp(user: WorkspaceSettingsNavUser): boolean {
  if (!user || user.is_super_admin) return false;
  const role = (user.role || '').toString().trim().toUpperCase();
  return Boolean(user.is_organization_owner) || role === 'ADMIN';
}

/** AI Knowledge is for admins (super admins included). */
export function canSeeWorkspaceAiKnowledge(user: WorkspaceSettingsNavUser): boolean {
  if (!user) return false;
  if (user.is_super_admin) return true;
  return (user.role || '').toString().trim().toUpperCase() === 'ADMIN';
}

export function visibleWorkspaceSettingsSections(user: WorkspaceSettingsNavUser): WorkspaceSettingsSection[] {
  return WORKSPACE_SETTINGS_SECTIONS.filter((s) => {
    if (s.id === 'mcp') return canSeeWorkspaceMcp(user);
    if (s.id === 'ai-knowledge') return canSeeWorkspaceAiKnowledge(user);
    return true;
  }).map((s) => s.id);
}

/** The section a URL asks for, falling back to profile when the user may not see it. */
export function resolveWorkspaceSettingsSection(
  raw: string | null,
  user: WorkspaceSettingsNavUser,
): WorkspaceSettingsSection {
  const requested = parseWorkspaceSettingsSection(raw);
  if (requested === 'mcp' && !canSeeWorkspaceMcp(user)) return 'profile';
  if (requested === 'ai-knowledge' && !canSeeWorkspaceAiKnowledge(user)) return 'profile';
  return requested;
}
